import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import { cn } from '@workspace/ui/lib/utils';

import { PriceText } from '~/components/Price/PriceText';
import type { Offer, Product } from '~/stores/CategoriesStore';
import {
  convertPenceToPounds,
  formatPriceWithCurrency,
} from '~/utils/formaters';

interface ProductMixAndMatchPriceProps {
  readonly item: Offer | Product;
  readonly className?: string;
}

const PRICE_FONT =
  '[font:var(--header-200-semibold)] desktop:[font:var(--header-400-semibold)]';

/** Mix & Match price and its threshold next to the regular price. */
const ProductMixAndMatchPrice = ({
  item,
  className,
}: ProductMixAndMatchPriceProps) => {
  const { t } = useTranslation();
  const promo = item.mixAndMatch;

  if (!promo) {
    return null;
  }

  const hasDiscount = item.discountPrice > 0 && item.discountPrice < item.price;

  return (
    <div
      className={cn(
        'desktop:mt-8 mt-3 flex flex-wrap items-start gap-x-6 gap-y-2',
        className,
      )}
    >
      <div className="flex flex-col gap-1 text-text-brand">
        <span className={PRICE_FONT}>
          <PriceText>
            {formatPriceWithCurrency(convertPenceToPounds(promo.price))}
          </PriceText>
        </span>
        <span className="[font:var(--text-200-medium)]">
          {t('phrases:mixAndMatchFromItems', { count: promo.threshold })}
        </span>
      </div>
      <div className="flex flex-col gap-1">
        <span className={cn('text-text-primary', PRICE_FONT)}>
          <PriceText>
            {formatPriceWithCurrency(convertPenceToPounds(promo.regularPrice))}
          </PriceText>
        </span>
        <span className="text-text-secondary [font:var(--text-200-regular)]">
          {t('phrases:mixAndMatchRegularPrice')}
        </span>
        {hasDiscount && (
          <span className="text-text-tertiary line-through [font:var(--text-100-medium)]">
            <PriceText>
              {formatPriceWithCurrency(item.priceFormatted)}
            </PriceText>
          </span>
        )}
      </div>
    </div>
  );
};

export default observer(ProductMixAndMatchPrice);
